import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { API, apiErr } from "../../lib/api";
import { fmtEuro, fmtDate } from "../../lib/format";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "../../components/ui/alert-dialog";
import { toast } from "sonner";
import { Download, Search, Loader2, Trash2 } from "lucide-react";

const STATI = ["Nuova", "Contattata", "Incarico acquisito", "Incarico firmato", "Gestione alleato", "Persa", "Chiusa senza esito"];
const ALL = "__all__";

const statoBadge = {
  "Nuova": "bg-[#E9F0F5] text-[#4F708C]",
  "Contattata": "bg-[#FEF9E7] text-[#8C7B2E]",
  "Incarico acquisito": "bg-[#EEF4EB] text-[#4F683E]",
  "Incarico firmato": "bg-[#DDEBD3] text-[#3B5A2A]",
  "Gestione alleato": "bg-[#E3EEF0] text-[#3E6B72]",
  "Persa": "bg-[#F3E7E9] text-[#8A5B62]",
  "Chiusa senza esito": "bg-[#FDF2F4] text-[#9E4756]",
};

export const Simulazioni = () => {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [canali, setCanali] = useState([]);
  const [filters, setFilters] = useState({
    canale: "", stato: "", credito_min: "", credito_max: "", data_da: "", data_a: "", q: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v !== "" && v != null) params[k] = v; });
      const r = await api.get("/admin/simulazioni", { params });
      setRows(r.data);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/admin/canali").then((r) => setCanali(r.data)).catch(() => {}); }, []);

  const up = (k, v) => setFilters((p) => ({ ...p, [k]: v }));

  const exportExcel = async () => {    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v !== "" && v != null) params.append(k, v); });
      const r = await api.get(`/admin/simulazioni/export?${params.toString()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "simulazioni.xlsx";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const deleteRow = async (id) => {
    try {
      await api.delete(`/admin/simulazioni/${id}`);
      toast.success("Simulazione eliminata.");
      setRows((prev) => prev.filter((x) => x.id !== id));
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const inputCls = "h-10 bg-white border-[#DDE5E2]";

  return (
    <div data-testid="admin-simulations-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl font-semibold text-[#2B3A42]">Simulazioni</h1>
        <Button data-testid="admin-export-excel-btn" onClick={exportExcel}
          className="gap-2 rounded-full bg-[#9CAF88] hover:bg-[#7C8F69] text-white">
          <Download className="h-4 w-4" /> Esporta Excel
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-[#DDE5E2] p-4 mb-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="space-y-1.5">
          <Label className="text-xs text-[#5B6E78]">Ricerca (Ragione sociale / P.IVA / Referente)</Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#5B6E78]" />
            <Input data-testid="filter-search" className={`${inputCls} pl-9`} value={filters.q}
              onChange={(e) => up("q", e.target.value)} placeholder="Cerca..." />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs text-[#5B6E78]">Canale</Label>
          <Select value={filters.canale || ALL} onValueChange={(v) => up("canale", v === ALL ? "" : v)}>
            <SelectTrigger data-testid="filter-canale" className={inputCls}><SelectValue placeholder="Tutti" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Tutti</SelectItem>
              {canali.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.nome}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs text-[#5B6E78]">Stato</Label>
          <Select value={filters.stato || ALL} onValueChange={(v) => up("stato", v === ALL ? "" : v)}>
            <SelectTrigger data-testid="filter-stato" className={inputCls}><SelectValue placeholder="Tutti" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Tutti</SelectItem>
              {STATI.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1.5">
            <Label className="text-xs text-[#5B6E78]">Credito min</Label>
            <Input data-testid="filter-credito-min" className={inputCls} inputMode="numeric" value={filters.credito_min}
              onChange={(e) => up("credito_min", e.target.value.replace(/[^\d.]/g, ""))} placeholder="0" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-[#5B6E78]">Credito max</Label>
            <Input data-testid="filter-credito-max" className={inputCls} inputMode="numeric" value={filters.credito_max}
              onChange={(e) => up("credito_max", e.target.value.replace(/[^\d.]/g, ""))} placeholder="—" />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs text-[#5B6E78]">Data da</Label>
          <Input data-testid="filter-data-da" type="date" className={inputCls} value={filters.data_da}
            onChange={(e) => up("data_da", e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs text-[#5B6E78]">Data a</Label>
          <Input data-testid="filter-data-a" type="date" className={inputCls} value={filters.data_a}
            onChange={(e) => up("data_a", e.target.value)} />
        </div>
        <div className="flex items-end">
          <Button variant="outline" data-testid="filter-reset"
            onClick={() => setFilters({ canale: "", stato: "", credito_min: "", credito_max: "", data_da: "", data_a: "", q: "" })}
            className="h-10 border-[#DDE5E2] text-[#5B6E78]">Azzera filtri</Button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-[#DDE5E2] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-simulations-table">
            <thead>
              <tr className="bg-[#EDF2F0] text-[#2B3A42] text-left">
                <th className="px-4 py-3 font-medium">Data</th>
                <th className="px-4 py-3 font-medium">Canale</th>
                <th className="px-4 py-3 font-medium">Ragione sociale</th>
                <th className="px-4 py-3 font-medium">P.IVA</th>
                <th className="px-4 py-3 font-medium">Referente</th>
                <th className="px-4 py-3 font-medium">Email</th>
                <th className="px-4 py-3 font-medium text-right">Credito totale</th>
                <th className="px-4 py-3 font-medium">Stato</th>
                <th className="px-4 py-3 font-medium text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-[#5B6E78]">
                  <Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-[#5B6E78]">Nessuna simulazione trovata.</td></tr>
              ) : rows.map((r) => (
                <tr key={r.id} data-testid={`sim-row-${r.id}`}
                  onClick={() => nav(`/admin/sim/${r.id}`)}
                  className="border-t border-[#DDE5E2] hover:bg-[#F5F8F7] cursor-pointer">
                  <td className="px-4 py-3 whitespace-nowrap">{fmtDate(r.created_at)}</td>
                  <td className="px-4 py-3">{r.canale_nome}</td>
                  <td className="px-4 py-3 font-medium">{r.ragione_sociale}</td>
                  <td className="px-4 py-3 tnum">{r.partita_iva}</td>
                  <td className="px-4 py-3">{r.referente}</td>
                  <td className="px-4 py-3">{r.email}</td>
                  <td className="px-4 py-3 text-right tnum font-medium">{fmtEuro(r.credito_totale)}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block rounded-full px-2.5 py-1 text-xs font-medium ${statoBadge[r.stato] || "bg-[#EDF2F0] text-[#5B6E78]"}`}>{r.stato}</span>
                  </td>
                  <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button data-testid={`sim-delete-btn-${r.id}`} variant="ghost" size="icon"
                          className="h-8 w-8 text-[#9E4756] hover:bg-[#FDF2F4]" onClick={(e) => e.stopPropagation()}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent onClick={(e) => e.stopPropagation()}>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Eliminare la simulazione?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Verrà eliminata definitivamente la simulazione di {r.ragione_sociale} ({r.partita_iva}). L'operazione è irreversibile.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Annulla</AlertDialogCancel>
                          <AlertDialogAction data-testid={`sim-delete-confirm-${r.id}`}
                            onClick={() => deleteRow(r.id)} className="bg-[#9E4756] hover:bg-[#833946]">Elimina</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <p className="text-xs text-[#5B6E78] mt-3">{rows.length} risultati</p>
    </div>
  );
};
